import { Grid, styled, Typography } from '@quillbot/design-system';
import React from 'react';

const features = [
  {
    name: 'Start for Free',
    desc: 'Ok, this one speaks for itself. Why pay for other citation makers when ours is both free and awesome?',
  },
  {
    name: 'Stay Up to Date',
    desc: 'Working in APA format? That manual is in its 7th edition. MLA format is in its 9th edition, and Chicago style is all the way to its 17th edition! We keep track of each citation style’s changes for you, so you can always be sure you’re up to date without having to buy those pricey manuals.',
  },
  {
    name: 'Be Accurate',
    desc: 'Our Citation Generator is verified by experts and PhDs alike to be accurate, so you can focus on sharing your unique ideas—instead of googling what part of a citation gets italicized. If you must search online, be warned that most of that information isn’t correct because it’s outdated.',
  },
  {
    name: 'Create In-Text & Full Citations',
    desc: 'Other free citation machines only create the full citation and leave off the in-text version. Or, even worse, they say it’s free, but then tell you that you have to pay for the in-text citation information, after you’ve spent your time adding all of your sources into their system. Not us!',
  },
  {
    name: 'Enjoy An Easy-to-Use Interface',
    desc: 'Our sleek interface is intuitive and uncluttered. It’s a joy to use, some might say, especially those who’ve had their time wasted by the other clunky, confusing citation creators out there.',
  },
  {
    name: 'Get Results Fast',
    desc: 'Do everything from finding to citing to editing a source’s citation in just a click or two. Save yourself from other citation makers that need to load page after page to create one citation—or worse, those that crash and make you start over.',
  },
  {
    name: 'Avoid Ads',
    desc: 'You’ll never have to deal with distractions like frustrating pop-ups, annoying tabs, or ads with us. We might not be a citation format, but that’s still not our style.',
  },
  {
    name: 'Search & Cite',
    desc: 'Don’t have the source on hand? Use our convenient search feature to look up the book or website sources you’re using.',
  },
  {
    name: 'Edit Easily',
    desc: 'Need to make a change to a citation? Add to or update the source or usage information with a couple of keystrokes or clicks.',
  },
  {
    name: 'Get Alerts for Missing Info',
    desc: 'When you create a citation, QuillBot will highlight any information that is missing or could be added for completeness.',
  },
  {
    name: 'Quickly Find & Review Source Material',
    desc: 'Use your Citation List as a convenient way to navigate back to review reference materials. Find the “Open Citation Source” in the three-dot menu in the top-right corner of each citation entry.',
  },
  {
    name: 'Adapt To Your Workflow',
    desc: 'Export your complete list of full citations to Microsoft Word, BibTeX, or CSL-JSON or by copying them to your clipboard. Adding in-text citations as you draft? (Good for you!) Easily copy each in-text citation to paste it into your draft as you work to save yourself time and energy later.',
  },
  {
    name: 'Autosave in Your Web Browser',
    desc: 'Once you create a citation, it is automatically saved to your web browser so that you can always come back and access or edit it later; no double-work necessary.',
  },
  {
    name: 'Change Style with One Click',
    desc: 'Working in MLA format but actually need to use APA? That change is just one drop-down click away. Simply choose the new style you want using the drop-down list, under Citation Style within your Citation List, and all of your citations will automatically be reformatted.',
  },
  {
    name: 'Save Time & Energy',
    desc: 'Once you understand how to cite, you’re free to lean into the “automation is the future” idea. Let us do the heavy lifting re: punctuation, italicizing, and all the other nit-picky citation stuff. Save your time and energy to focus on your ideas and arguments.',
  },
  {
    name: 'Get More Support',
    desc: 'QuillBot has many other tools to help you smash all of your writing and research goals by maximizing your efficiency and upgrading the quality of your work. We’re no one-trick pony.',
  },
];

type FeatureProps = typeof features[number];
const Feature = ({ name, desc }: FeatureProps) => (
  <Grid item className="feature-card" xs={12} sm={6}>
    <Typography component="div" fontSize="20px" fontWeight={600}>
      {name}
    </Typography>

    <Typography component="p" fontSize="16px" fontWeight={400} pt="5px">
      {desc}
    </Typography>
  </Grid>
);

const StyledGrid = styled(Grid)`
  background-color: ${({ theme }) => theme.specialColors.CG_INFO_BG};
  background-image: url('/images/coWriter/cg-creative-1.svg');
  background-repeat: no-repeat;
  background-position: top right;
  justify-content: center;
  padding-bottom: 30px;
  align-items: center;
  position: relative;

  & > h2 {
    font-weight: 400;
    font-size: 24px;
    text-align: center;
  }

  & .feature-card {
    justify-content: flex-start;
    align-content: flex-start;
    margin-bottom: 30px;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    & .feature-card {
      padding-left: 16px;
      padding-right: 16px;
    }
  }

  ${({ theme }) => theme.breakpoints.up('sm')} {
    & .feature-card:nth-child(odd) {
      padding-left: 8px;
      padding-right: 20px;
    }

    & .feature-card:nth-child(even) {
      padding-left: 20px;
      padding-right: 8px;
    }
  }
`;

const Features = () => (
  <StyledGrid container flexDirection="column">
    <Typography component="h2" py="40px">
      Citation Generator Attributes You’ll Love
    </Typography>

    <Grid container maxWidth="1080px" margin="auto" flexWrap="wrap">
      {features.map(({ name, desc }) => (
        <Feature key={name} name={name} desc={desc} />
      ))}
    </Grid>
  </StyledGrid>
);

export default Features;
